import React, { useState } from 'react';
import { LogIn, Lock, Mail, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './Login.css';

const Login = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Por favor complete todos los campos');
      return;
    }

    setIsLoading(true);
    const success = await login(email, password);
    setIsLoading(false);
    
    if (!success) {
      setError('Email o contraseña incorrectos');
    }
  };

  return (
    <div className="login-container" data-testid="login-view">
      <div className="login-background">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <img 
                src="/logo-alerta360.png" 
                alt="ALERTA 360" 
                style={{ 
                  width: '100%',
                  maxWidth: '280px',
                  height: 'auto',
                  marginBottom: '0.5rem'
                }}
              />
            </div>
            <p className="login-subtitle">Comisión Nacional contra la Corrupción</p>
          </div>

          {!showResetPassword ? (
            <form onSubmit={handleSubmit} className="login-form">
              {error && (
                <div className="login-error" data-testid="login-error">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">
                  <Mail size={18} />
                  Correo Electrónico
                </label>
                <input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  placeholder="usuario@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">
                  <Lock size={18} />
                  Contraseña
                </label>
                <div className="password-input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="password-input"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="btn-login"
                disabled={isLoading}
                data-testid="login-button"
              >
                <LogIn size={20} />
                {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </button>

              <button
                type="button"
                className="btn-reset-link"
                onClick={() => setShowResetPassword(true)}
              >
                ¿Olvidó su contraseña?
              </button>
            </form>
          ) : (
            <ResetPasswordForm onBack={() => setShowResetPassword(false)} />
          )}

          <div className="login-footer">
            <p>ALERTA 360 — Sistema Integral de Gestión de Casos © {new Date().getFullYear()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ResetPasswordForm = ({ onBack }) => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !newPassword || !confirmPassword) {
      setError('Complete todos los campos');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (newPassword.length < 5) {
      setError('La contraseña debe tener al menos 5 caracteres');
      return;
    }

    setIsLoading(true);
    const success = await resetPassword(email, newPassword);
    setIsLoading(false);

    if (success) {
      setTimeout(() => onBack(), 2000);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="login-form">
      <h3 className="reset-title">Recuperar Contraseña</h3>
      
      {error && (
        <div className="login-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="reset-email">Correo Electrónico</label>
        <input
          id="reset-email"
          type="email"
          placeholder="usuario@ejemplo.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="new-password">Nueva Contraseña</label>
        <input
          id="new-password"
          type="password"
          placeholder="Nueva contraseña"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="confirm-password">Confirmar Contraseña</label>
        <input
          id="confirm-password"
          type="password"
          placeholder="Confirme la contraseña"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isLoading}
        />
      </div>

      <button
        type="submit"
        className="btn-login"
        disabled={isLoading}
      >
        {isLoading ? 'Actualizando...' : 'Actualizar Contraseña'}
      </button>

      <button
        type="button"
        className="btn-reset-link"
        onClick={onBack}
      >
        Volver al inicio de sesión
      </button>
    </form>
  );
};

export default Login;
